<script setup>
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['submit']);
const { t } = useI18n();

const dialogRef = ref(null);
const isSubmitting = ref(false);
const editingCategoryId = ref(null);

const form = reactive({
  name: '',
  description: '',
});

const title = ref('');

function resetForm() {
  editingCategoryId.value = null;
  form.name = '';
  form.description = '';
  isSubmitting.value = false;
}

function open(category = null) {
  editingCategoryId.value = category?.id || null;
  form.name = category?.name || '';
  form.description = category?.description || '';
  title.value = editingCategoryId.value
    ? t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.MODAL.EDIT_TITLE')
    : t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.MODAL.CREATE_TITLE');
  dialogRef.value?.open();
}

function close() {
  dialogRef.value?.close();
}

async function handleConfirm() {
  if (!form.name.trim()) return;

  isSubmitting.value = true;
  try {
    await emit('submit', {
      id: editingCategoryId.value,
      name: form.name.trim(),
      description: form.description.trim(),
    });
    close();
  } finally {
    isSubmitting.value = false;
  }
}

function handleClose() {
  resetForm();
}

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="title"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="$t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.FORM.NAME')"
      />
      <TextArea
        v-model="form.description"
        :label="
          $t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.FORM.DESCRIPTION')
        "
      />
    </div>
    <template #footer>
      <div class="flex w-full justify-end gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="$t('DIALOG.BUTTONS.CANCEL')"
          @click="close"
        />
        <Button
          blue
          :label="$t('DIALOG.BUTTONS.CONFIRM')"
          :is-loading="isSubmitting"
          :disabled="!form.name.trim()"
          @click="handleConfirm"
        />
      </div>
    </template>
  </Dialog>
</template>
